import React, {useEffect, useId, useRef, useState} from 'react';
import {Input, Button} from 'antd';
import { CloseRounded, CheckRounded, EditRounded } from "@mui/icons-material";

const InputAntdInline = ({
                             value = '',
                             textType = 'h2',
                             onSave,
                             onCancel,
                             className = '',
                             isLoading = false,
                             minWidth = 300,
                             fullWidth = false,
                             ariaLabel, // Optional custom aria-label
                             fieldLabel, // Optional field description for screen readers
                             placeholder = '',
                             inputPlaceholder, // Optional separate placeholder for input field
                             isEditing: isEditingProp,
                             onEditingChange,
                             ...textProps
                         }) => {
    const isControlled = isEditingProp !== undefined;
    const [editInput, setEditInput] = useState(false);
    const editInputState = isControlled ? isEditingProp : editInput;

    const setEditInputState = (val) => {
        if (isControlled) {
            onEditingChange?.(val);
        } else {
            setEditInput(val);
        }
    };
    const [fieldValue, setFieldValue] = useState(value);
    const [lastValue, setLastValue] = useState(value);
    const [inputWidth, setInputWidth] = useState(minWidth);
    const [isHovered, setIsHovered] = useState(false);

    const textMeasureRef = useRef(null);
    const inputRef = useRef(null);
    const cancelButtonRef = useRef(null);
    const saveButtonRef = useRef(null);
    const fieldDescriptionId = useId();

    useEffect(() => {
        if (textMeasureRef.current) {
            const measuredWidth = textMeasureRef.current.offsetWidth;
            setInputWidth(Math.max(measuredWidth + 20, minWidth));
        }
    }, [fieldValue, minWidth]);

    useEffect(() => {
        setFieldValue(value);
    }, [value]);

    const handleSave = () => {
        setEditInputState(false);
        setIsHovered(false);
        if(lastValue !== fieldValue){
            onSave?.(fieldValue);
            setLastValue(fieldValue);
        }
    }

    const handleCancel = () => {
        setFieldValue(value);
        setEditInputState(false);
        setIsHovered(false);
        onCancel?.();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            handleCancel();
        } else if (e.key === 'Tab') {
            if (!e.shiftKey) {
                if (e.target === inputRef.current) {
                    e.preventDefault();
                    cancelButtonRef.current?.focus();
                } else if (e.target === cancelButtonRef.current) {
                    e.preventDefault();
                    saveButtonRef.current?.focus();
                }
            } else {
                if (e.target === saveButtonRef.current) {
                    e.preventDefault();
                    cancelButtonRef.current?.focus();
                } else if (e.target === cancelButtonRef.current) {
                    e.preventDefault();
                    inputRef.current?.focus();
                }
            }
        }
    };

    const TextElement = textType;

    const defaultFieldLabel = fieldLabel || `${textType} field`;
    const editButtonAriaLabel = ariaLabel || `Edit ${defaultFieldLabel}: ${fieldValue}`;
    const inputAriaLabel = `Edit ${defaultFieldLabel}`;
    const addArticle = (word) => /^[aeiou]/i.test(word) ? `Add an ${word}` : `Add a ${word}`;
    const placeholderText = addArticle(placeholder || defaultFieldLabel);

    return (
        <div className={`ch-editable-input-wrapper ${className}`} style={fullWidth ? { width: '100%' } : undefined}>
            {!editInputState && (
                <div
                    className="ch-inline-editor-container"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        position: 'relative'
                    }}
                >
                    <span
                        className="ch-sr-only"
                        id={`field-description-${fieldDescriptionId}`}
                    >
                        {defaultFieldLabel} with value: {fieldValue || placeholderText}
                    </span>
                    <TextElement>
                        <button
                            className={'ch-inline-editor-button'}
                            onClick={() => setEditInputState(true)}
                            aria-label={value === '' ? placeholderText : editButtonAriaLabel}
                            aria-describedby={`field-description-${fieldDescriptionId}`}
                            {...textProps}
                        >
                            <span aria-hidden="true">
                                {fieldValue || <em style={{ opacity: 0.9 }}>{placeholderText}</em>}
                            </span>
                        </button>
                    </TextElement>
                    {!isControlled && (
                        <EditRounded
                            aria-hidden="true"
                            style={{
                                fontSize: '18px',
                                color: '#666',
                                opacity: isHovered ? 0.7 : 0,
                                transition: 'opacity 0.2s ease'
                            }}
                        />
                    )}
                </div>
            )}

            {editInputState && (
                <Input
                    ref={inputRef}
                    value={fieldValue}
                    onChange={(e) => setFieldValue(e.target.value)}
                    style={{ width: fullWidth ? '100%' : inputWidth }}
                    autoFocus
                    disabled={isLoading}
                    onClick={() => setEditInputState(true)}
                    placeholder={inputPlaceholder || placeholder}
                    onPressEnter={handleSave}
                    onKeyDown={handleKeyDown}
                    aria-label={inputAriaLabel}
                    onBlur={(e) => {
                        const relatedTarget = e.relatedTarget;
                        if (relatedTarget !== cancelButtonRef.current &&
                            relatedTarget !== saveButtonRef.current) {
                            handleSave();
                        }
                    }}
                />
            )}

            {editInputState && (
                <div className={'ch-editable-input-actions'}>
                    <Button
                        ref={cancelButtonRef}
                        onClick={handleCancel}
                        disabled={isLoading}
                        aria-label={`Cancel editing ${defaultFieldLabel}`}
                        className={'ch-editable-input-action-button'}
                        tabIndex={0}
                    >
                        <CloseRounded aria-hidden="true" />
                    </Button>
                    <Button
                        ref={saveButtonRef}
                        type={'primary'}
                        loading={isLoading}
                        onClick={handleSave}
                        aria-label={`Save changes to ${defaultFieldLabel}`}
                        className={'ch-editable-input-action-button'}
                        tabIndex={0}
                    >
                        {!isLoading && <CheckRounded aria-hidden="true" />}
                    </Button>
                </div>
            )}
            <span
                ref={textMeasureRef}
                style={{
                    position: 'absolute',
                    visibility: 'hidden',
                    whiteSpace: 'pre',
                    fontSize: '14px',
                    fontFamily: '"Open Sans", "Segoe UI", Roboto, "Helvetica Neue", Arial',
                    padding: '4px 11px'
                }}
                aria-hidden={true}
            >
                {fieldValue || ' '}
            </span>
        </div>
    );
};

export default InputAntdInline;